<template>
	<div class="last-step">
		<template v-if="loading">
			<Loading :fun-fact="false"/>
			<div class="mt-4 text-center">
				<h3>Dein Job-Profil wird angelegt, das dauert ein paar Augenblicke.<br/>Bitte schließe diese Seite
					nicht.</h3>
			</div>
		</template>
		<template v-else>
			<h1>{{ title }}</h1>
			<div class="intro">
				<p>
					Wir haben dir ein E-Mail gesendet an<br/>
					<span class="pl-4 color-primary">{{ emailToShow }}</span><br/>
				</p>
				<p>Bitte klicke im E-Mail den Link, um dein Profil zu aktivieren.</p>
			</div>
			<template v-if="customerId">
				<div class="action-container">
					<div>E-Mail nicht erhalten?</div>
					<div class="pl-4">
						<Loading v-if="resending" small inline :block-element="false"/>
						<a v-else class="icon-link" href="#" @click.prevent="resend">
							<Icon icon="mail"/>
							Neu senden
						</a>
					</div>
				</div>
				<div class="action-container">
					<div>E-Mail Adresse falsch?</div>
					<div class="pl-4">
						<a class="icon-link" href="#" @click.prevent="correctEmail= true">
							<Icon icon="settings"/>
							E-Mail Adresse bearbeiten
						</a>
						<FormGroup
							v-if="correctEmail"
							:state="emailValid"
							:invalid-feedback="emailError"
							feedback-beneath
						>
							<div class="correct-email">
								<div>
									<b-input v-model="email" type="email"/>
								</div>
								<div>
									<Button
										text="E-Mail bestätigen"
										icon="checkmark"
										:disabled="!emailValid || resending"
										@click="resend"
									/>
								</div>
							</div>
						</FormGroup>
					</div>
				</div>
			</template>
		</template>
	</div>
</template>

<script>
import FormGroup from "@/vue/common/form/Group";
import Icon from "@/vue/icon/Icon";
import Init from "@/api/customer/Init";
import Loading from "@/vue/common/Loading";
import {isDefined} from "@/helper/Value";
import {isEmptyOrNull} from "@/helper/String";
import * as EmailValidator from 'email-validator';
import Button from "@/vue/common/Button";
import * as Toast from "@/helper/layout/Toast";
import flatten from "@/helper/error/Errors";
import Resend from "@/api/customer/email/Resend";
import {prepare} from "@/helper/job-profile/PreSavePreparation";
import CheckEmailConfirmation from "@/api/customer/security/CheckEmailConfirmation";

const CONFIRMATION_INTERVAL = 5;
const MAX_CONFIRMATION_CHECKS = (60 / CONFIRMATION_INTERVAL) * 15; // every 5 seconds for 15 minutes

export default {

	components: {
		Button,
		Loading,
		FormGroup,
		Icon,
	},

	mounted ()
	{
		this.initCustomer();
	},

	props: {
		title: {
			type: String,
			required: true,
		},
		formData: {
			type: Object,
			required: true,
		},
		careerLevelEligible: {
			type: Boolean,
			required: true,
		},
		organisationalAreaEligible: {
			type: Boolean,
			required: true,
		},
	},

	data ()
	{
		const email = this.formData.step5.email;

		return {
			loading: true,
			customerId: undefined,
			correctEmail: false,
			email,
			emailToShow: email,
			resending: false,
			confirmationChecks: 0,
		};
	},

	computed: {
		emailValid ()
		{
			return !isDefined(this.emailError);
		},
		emailError ()
		{
			if (isEmptyOrNull(this.email))
			{
				return 'Pflichtfeld';
			}

			if (!EmailValidator.validate(this.email))
			{
				return 'E-Mail Adresse unvollständig/ungültig';
			}

			return null;
		},
	},

	methods: {
		async resend ()
		{
			if (this.resending)
			{
				return;
			}

			this.resending = true;

			try
			{
				await new Resend()
					.setCustomerId(this.customerId)
					.setEmail(this.email)
					.call();

				this.emailToShow = this.email;
				this.correctEmail = false;
			}
			catch (e)
			{
				Toast.error(this, flatten(e.errors));
			}

			this.resending = false;
		},
		async initCustomer ()
		{
			const data = {
				functions: this.formData.step1.functions,
				careerLevels: this.careerLevelEligible
					? this.formData.step3.careerLevels
					: undefined,
				organisationalArea: this.organisationalAreaEligible ? this.formData.step3.organisationalArea : undefined,
				employmentMode: this.formData.step2.employmentModes,
				workingTime: this.formData.step2.workingTimes,
				placeOfWork: this.formData.step4.placeOfWork,
				homeOffice: {
					...this.formData.step4.homeOffice,
					advanced: false
				},
				customerType: this.formData.step5.customerType,
				companyType: [{item: this.formData.step5.companyType}],
				companySize: [{item: this.formData.step5.companySize}],
				firstName: this.formData.step5.firstName,
				email: this.formData.step5.email,
				password: this.formData.step5.password,
			};

			const response = await new Init()
				.setData(
					prepare(data)
				)
				.call();

			this.customerId = response.data.customerId;

			if (this.customerId)
			{
				this.checkConfirmationInterval = setInterval(async () =>
				{
					try
					{
						const response = await new CheckEmailConfirmation()
							.setId(this.customerId)
							.call();

						if (response.data.confirmed)
						{
							location.href = '/auth/login?activated=1&email=' + this.emailToShow;
						}
					}
					catch (e)
					{
						// do nothing
					}

					this.confirmationChecks++;

					if (this.confirmationChecks === MAX_CONFIRMATION_CHECKS)
					{
						clearInterval(this.checkConfirmationInterval);
					}

				}, CONFIRMATION_INTERVAL * 1000);
			}

			this.loading = false;
		}
	}
}
</script>
